import { useEffect, useRef, useState } from "react";
import { MoreVertical } from "lucide-react";
import { Box, Flex, IconButton } from "@radix-ui/themes";

interface DropdownMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
}

interface DropdownMenuProps {
  id?: string | number;
  className?: string;
  items: DropdownMenuItem[];
  icon?: React.ReactNode;
}

export default function DropdownMenu({ items, icon }: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const calculatePosition = () => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 160; // minWidth from styles
    const dropdownHeight = items.length * 40; // approximate height per item

    // Calculate initial position (below and to the right of button)
    let top = buttonRect.bottom + 4; // 4px gap
    let left = buttonRect.right - dropdownWidth;

    // Ensure dropdown doesn't go off the right edge
    if (left < 8) {
      left = 8;
    }

    // Ensure dropdown doesn't go off the left edge
    if (left + dropdownWidth > window.innerWidth - 8) {
      left = window.innerWidth - dropdownWidth - 8;
    }

    // If dropdown would go below viewport, show it above the button
    if (top + dropdownHeight > window.innerHeight - 8) {
      top = buttonRect.top - dropdownHeight - 4;
    }

    // Ensure dropdown doesn't go above viewport
    if (top < 8) {
      top = 8;
    }

    setPosition({ top, left });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleScroll() {
      if (isOpen) {
        calculatePosition();
      }
    }

    function handleResize() {
      if (isOpen) {
        calculatePosition();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, items.length]);

  useEffect(() => {
    if (isOpen) {
      calculatePosition();
    }
  }, [isOpen, items.length]);

  return (
    <>
      <Flex ref={containerRef} style={{ position: "relative" }} align="center">
        <IconButton
          ref={buttonRef}
          data-testid="dropdown-menu-button"
          variant="ghost"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          {icon || (
            <MoreVertical
              style={{ width: "1rem", height: "1rem", color: "var(--gray-9)" }}
            />
          )}
        </IconButton>
      </Flex>

      {isOpen && (
        <Box
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: `${position.top}px`,
            left: `${position.left}px`,
            minWidth: "160px",
            backgroundColor: "var(--color-surface)",
            border: "1px solid var(--gray-a6)",
            borderRadius: "var(--radius-3)",
            boxShadow: "var(--shadow-5)",
            zIndex: 999999,
            overflow: "hidden",
          }}
        >
          {items.map((item, index) => (
            <Box
              key={index}
              onClick={(e) => {
                if (!item.disabled) {
                  e.preventDefault();
                  e.stopPropagation();
                  item.onClick();
                  setIsOpen(false);
                }
              }}
              style={{
                padding: "8px 16px",
                cursor: item.disabled ? "not-allowed" : "pointer",
                opacity: item.disabled ? 0.5 : 1,
                transition: "all 150ms ease",
                fontSize: "var(--font-size-2)",
                display: "flex",
                alignItems: "center",
                width: "100%",
                textAlign: "left",
                color: "var(--gray-12)",
                backgroundColor: "var(--color-surface)",
              }}
              onMouseEnter={(e) => {
                if (!item.disabled) {
                  e.currentTarget.style.backgroundColor = "var(--accent-3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!item.disabled) {
                  e.currentTarget.style.backgroundColor =
                    "var(--color-surface)";
                }
              }}
            >
              {item.icon && (
                <Box style={{ marginRight: "8px" }}>{item.icon}</Box>
              )}
              <Box>{item.label}</Box>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
}
